import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "ATC Mold & Water Restoration";
const SITE_URL = "https://atcmoldandwater.com";
const DEFAULT_DESC =
  "ATC Mold & Water Restoration — Thermification™ equipment for professional mold remediation, water damage restoration, and disaster-response drying. Chemical-free, ozone-free indirect-fired dry heat and high-volume air-exchange platform trusted by restoration pros.";
const DEFAULT_IMG = `${SITE_URL}/atc-logo.png`;
const TWITTER_HANDLE = "@atcmoldandwater";

/**
 * Unified SEO / structured-data helper.
 * Usage:
 *   <SEO
 *     title="Equipment"
 *     description="..."
 *     path="/equipment"
 *     schema={ ...jsonld }
 *     breadcrumbs={[{ name: "Equipment", path: "/equipment" }]}
 *   />
 */
const SEO = ({
  title,
  description = DEFAULT_DESC,
  path = "/",
  image = DEFAULT_IMG,
  schema, // additional JSON-LD block (object or array)
  breadcrumbs, // optional [{ name, path }] — Home is auto-prepended
  noindex = false,
}) => {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : `${SITE_NAME} — Thermification™ Restoration Equipment`;
  const url = SITE_URL + (path.startsWith("/") ? path : `/${path}`);

  // Sitewide Organization + OnlineStore schema (equipment retailer / manufacturer).
  const organizationLd = {
    "@context": "https://schema.org",
    "@type": ["Organization", "OnlineStore"],
    "@id": SITE_URL + "/#organization",
    name: SITE_NAME,
    alternateName: ["ATC Mold and Water", "ATC Restoration"],
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      url: DEFAULT_IMG,
      width: 512,
      height: 512,
    },
    image: DEFAULT_IMG,
    description: DEFAULT_DESC,
    slogan: "Powered by Thermification™",
    email: "media@atcpestcontrol.com",
    knowsAbout: [
      "Thermification",
      "Mold Remediation Equipment",
      "Water Damage Restoration Equipment",
      "Indirect-Fired Dry Heat Drying",
      "High-Volume Air Exchange",
      "Structural Drying",
      "HEPA Air Filtration",
      "Restoration Industry Equipment",
    ],
    sameAs: [],
  };

  const webSiteLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": SITE_URL + "/#website",
    url: SITE_URL,
    name: SITE_NAME,
    publisher: { "@id": SITE_URL + "/#organization" },
    inLanguage: "en-US",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/equipment?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };

  // Auto-build breadcrumbs (always prepend Home).
  const crumbTrail = [{ name: "Home", path: "/" }, ...(breadcrumbs || [])];
  const breadcrumbLd =
    crumbTrail.length > 1
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: crumbTrail.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.name,
            item: SITE_URL + (c.path.startsWith("/") ? c.path : `/${c.path}`),
          })),
        }
      : null;

  // Support: single object, array of objects, or nothing
  const extraSchemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  return (
    <Helmet>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Organization + WebSite — sitewide */}
      <script type="application/ld+json">
        {JSON.stringify(organizationLd)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(webSiteLd)}
      </script>
      {breadcrumbLd && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbLd)}
        </script>
      )}

      {/* Page-specific structured data */}
      {extraSchemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {typeof s === "string" ? s : JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
export { SITE_URL, SITE_NAME };
